import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { CircularProgress } from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { Button, Panel, colors } from 'src/components/app-ui'
import { languages } from 'src/model/languages'
import {
  shopLanguageGetService,
  shopLanguageUpdateService,
  shopTextsGetService,
  shopTextsUpdateService,
} from 'src/api/shop'
import safeTrack from 'src/utils/safe-track'
import useFormStyles from './form-styles'

const useStyles = makeStyles({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
  },
  picker: {
    width: 220,
    flex: 'none',
  },
  groups: {
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    overflow: 'hidden',
  },
  group: {
    '& + &': {
      borderTop: `1px solid ${colors.border}`,
    },
  },
  groupHead: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    border: 0,
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    '&:hover': {
      background: '#F9FAFB',
    },
  },
  groupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
  },
  count: {
    fontSize: '12px',
    fontWeight: 400,
    color: colors.subtle,
  },
  edited: {
    padding: '2px 6px',
    borderRadius: '4px',
    background: '#FEF3DD',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: colors.warningDark,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '14px',
    padding: '4px 16px 18px',
  },
  wide: {
    gridColumn: '1 / -1',
  },
  textarea: {
    minHeight: 160,
    resize: 'vertical',
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: '12px',
    lineHeight: 1.5,
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
  },
  reset: {
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    fontWeight: 500,
    color: colors.primary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
})

const EDITED_BORDER = '#FB9D01'

const errorMessage = (error, fallback) =>
  error?.response?.data?.operationMessage || fallback

const humanize = code =>
  code
    .replace(/[-_.]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, letter => letter.toUpperCase())

// Email templates are whole HTML documents: edit them in a large box.
const isLongText = text => text.length > 120 || /<\w/.test(text)

const isKnownLanguage = code =>
  languages.some(language => language.countryCode === code)

// The language list has a few entries per code (English variants); show each code once.
const LANGUAGE_OPTIONS = languages.filter(
  (language, index) =>
    languages.findIndex(item => item.countryCode === language.countryCode) ===
    index,
)

const LanguageSelect = ({ value, onChange, workspaceLanguage, disabled }) => {
  const form = useFormStyles()
  return (
    <select
      className={form.select}
      value={value}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
    >
      {LANGUAGE_OPTIONS.map(language => (
        <option key={language.countryCode} value={language.countryCode}>
          {language.name}
          {workspaceLanguage === language.countryCode ? ' (workspace)' : ''}
        </option>
      ))}
    </select>
  )
}

/**
 * Workspace > Language: the workspace language (login, the portal, path
 * pages, learner emails and new courses) and the built-in texts of every
 * language. A text edit applies to every course in that language. Opening
 * with ?lang=xx (a course's "Edit interface text") picks that language.
 */
const LanguageTab = () => {
  const classes = useStyles()
  const form = useFormStyles()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()

  const requestedLanguage = new URLSearchParams(location.search).get('lang')

  const [workspaceLanguage, setWorkspaceLanguage] = useState(null)
  const [language, setLanguage] = useState(
    isKnownLanguage(requestedLanguage) ? requestedLanguage : null,
  )
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [open, setOpen] = useState({})
  // Unsaved changes of the shown language: { [key]: text }.
  const [changes, setChanges] = useState({})

  useEffect(() => {
    let cancelled = false
    shopLanguageGetService()
      .then(response => {
        if (cancelled) return
        const code = response?.data?.languageCode || 'en'
        setWorkspaceLanguage(code)
        setLanguage(current => current || code)
      })
      .catch(error => {
        if (cancelled) return
        setWorkspaceLanguage('en')
        setLanguage(current => current || 'en')
        enqueueSnackbar(errorMessage(error, 'Could not load the language.'), {
          variant: 'error',
        })
      })
    return () => {
      cancelled = true
    }
  }, [enqueueSnackbar])

  useEffect(() => {
    if (!language) return undefined
    let cancelled = false
    setLoading(true)
    shopTextsGetService(language)
      .then(response => {
        if (cancelled) return
        setGroups(response?.data?.groups || [])
        setChanges({})
      })
      .catch(error => {
        if (cancelled) return
        setGroups([])
        enqueueSnackbar(errorMessage(error, 'Could not load the texts.'), {
          variant: 'error',
        })
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [language, enqueueSnackbar])

  const dirty = Object.keys(changes).length > 0

  const changeWorkspaceLanguage = code => {
    const previous = workspaceLanguage
    setWorkspaceLanguage(code)
    shopLanguageUpdateService(code)
      .then(() => {
        safeTrack('changed_workspace_language', { from: previous, to: code })
        enqueueSnackbar('Saved', { variant: 'success' })
      })
      .catch(error => {
        setWorkspaceLanguage(previous)
        enqueueSnackbar(errorMessage(error, 'Could not save the language.'), {
          variant: 'error',
        })
      })
  }

  const changeLanguage = code => {
    if (
      dirty &&
      !window.confirm(
        'Discard the changes you have not saved in this language?',
      )
    ) {
      return
    }
    setLanguage(code)
  }

  // The text shown in a field: the unsaved change, the saved edit or the default.
  const valueOf = useCallback(
    text =>
      text.key in changes ? changes[text.key] : text.text ?? text.defaultText,
    [changes],
  )
  const isEdited = text => valueOf(text) !== text.defaultText

  const setText = (text, value) =>
    setChanges(current => {
      const next = { ...current }
      // Back to what is saved: nothing to save for this text.
      if (value === (text.text ?? text.defaultText)) delete next[text.key]
      else next[text.key] = value
      return next
    })

  const save = () => {
    const texts = Object.fromEntries(
      groups
        .flatMap(group => group.texts)
        .filter(text => text.key in changes)
        .map(text => {
          const value = changes[text.key]
          return [
            text.key,
            value === text.defaultText || value === '' ? null : value,
          ]
        }),
    )
    setSaving(true)
    shopTextsUpdateService(language, texts)
      .then(response => {
        const values = Object.values(texts)
        safeTrack('saved_workspace_texts', {
          language,
          texts_changed: values.filter(value => value !== null).length,
          texts_reset: values.filter(value => value === null).length,
          groups: groups
            .filter(group => group.texts.some(text => text.key in texts))
            .map(group => group.code),
        })
        setGroups(response?.data?.groups || [])
        setChanges({})
        enqueueSnackbar('Saved', { variant: 'success' })
      })
      .catch(error =>
        enqueueSnackbar(errorMessage(error, 'Could not save the texts.'), {
          variant: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  const isOpen = (code, index) => (code in open ? open[code] : index === 0)

  return (
    <div className={form.stack}>
      <Panel className={form.card}>
        <div className={classes.row}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={form.cardTitle}>Default language</div>
            <div className={form.cardDesc}>
              Used on shared screens like login, the portal and emails. New
              courses start in this language and can switch to another one.
            </div>
          </div>
          <div className={classes.picker}>
            <LanguageSelect
              value={workspaceLanguage || 'en'}
              disabled={!workspaceLanguage}
              onChange={changeWorkspaceLanguage}
            />
          </div>
        </div>
      </Panel>

      <Panel className={form.card}>
        <div className={classes.row}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={form.cardTitle}>Built-in texts per language</div>
            <div className={form.cardDesc}>
              Buttons and system messages such as Next, Quiz or Sign in. Each
              course shows the texts of the language it is set to, so you can
              edit every language separately.
            </div>
          </div>
          <div className={classes.picker}>
            <LanguageSelect
              value={language || 'en'}
              disabled={!language}
              onChange={changeLanguage}
              workspaceLanguage={workspaceLanguage}
            />
          </div>
        </div>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className={classes.groups}>
            {groups.map((group, index) => {
              const expanded = isOpen(group.code, index)
              const editedCount = group.texts.filter(isEdited).length
              return (
                <div key={group.code} className={classes.group}>
                  <button
                    type="button"
                    className={classes.groupHead}
                    onClick={() =>
                      setOpen(all => ({ ...all, [group.code]: !expanded }))
                    }
                  >
                    <span className={classes.groupTitle}>
                      {group.label}
                      <span className={classes.count}>
                        {group.texts.length} texts
                      </span>
                      {editedCount > 0 && (
                        <span className={classes.edited}>
                          {editedCount} EDITED
                        </span>
                      )}
                    </span>
                    <Icon
                      icon="tabler:chevron-down"
                      width={18}
                      height={18}
                      color={colors.muted}
                      style={{
                        transform: expanded ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </button>
                  {expanded && (
                    <div className={classes.fields}>
                      {group.texts.map(text => {
                        const edited = isEdited(text)
                        const long = isLongText(text.defaultText)
                        const inputProps = {
                          className: [form.input, long && classes.textarea]
                            .filter(Boolean)
                            .join(' '),
                          style: edited
                            ? { borderColor: EDITED_BORDER }
                            : undefined,
                          value: valueOf(text),
                          onChange: event => setText(text, event.target.value),
                        }
                        return (
                          <label
                            key={text.key}
                            className={[form.field, long && classes.wide]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <span className={classes.label}>
                              {text.label && text.label !== text.key
                                ? text.label
                                : humanize(text.key)}
                              {edited && (
                                <button
                                  type="button"
                                  className={classes.reset}
                                  onClick={event => {
                                    event.preventDefault()
                                    setText(text, text.defaultText)
                                  }}
                                >
                                  Reset
                                </button>
                              )}
                            </span>
                            {long ? (
                              <textarea {...inputProps} />
                            ) : (
                              <input {...inputProps} />
                            )}
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className={classes.footer}>
          <Button disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Panel>
    </div>
  )
}

export default LanguageTab
